import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {getReadableYouTubePrivacySetting, YouTubePrivacySettings} from '@Components/social-media/post.vo';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {List} from '@Components/list';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Dropdown} from '@Components/dropdown-v2';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {noop} from '@Utils/general.util';
import {updateYouTubePublishingParamPrivacySettings} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {usePostPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import platformPublishFieldsStyles from '../platform-publish-fields/platform-publish-fields.module.scss';
import styles from './youtube-privacy-selector.module.scss';
import {useAppDispatch} from '@/hooks';
import {clearList} from '@Components/list/list-reducer';

interface YouTubePrivacySelectorProps {
  account: SocialAccount;
  onSelectOption?: (selectedOption: YouTubePrivacySettings) => void;
}

export function YouTubePrivacySelector({account, onSelectOption = noop}: YouTubePrivacySelectorProps): ReactElement<any> | null {
  const [selectedPrivacySettings, setSelectedPrivacySettings] = useState(YouTubePrivacySettings.PUBLIC);
  const [dropdownLabel, setDropdownLabel] = useState(getReadableYouTubePrivacySetting(selectedPrivacySettings));
  const publishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const dispatch = useAppDispatch();
  const listId = `youtube-account-sort-dropdown-list-${account.id}`;

  const getYouTubeSpecificId = (privacySetting: YouTubePrivacySettings): string => {
    return `youtube-${account.id}-${privacySetting}`;
  };

  const getListItemByPrivacySetting = (privacySetting: YouTubePrivacySettings, isUnmargined = false): ListItemStorage => {
    return {
      id: getYouTubeSpecificId(privacySetting),
      isSelected: selectedPrivacySettings === privacySetting,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: getReadableYouTubePrivacySetting(privacySetting),
      isItemUnmargined: isUnmargined,
      onClick(_listId?: string, listItemId?: string): void {
        onSelectOption(privacySetting);

        if (listItemId === getYouTubeSpecificId(privacySetting)) {
          setDropdownLabel(getReadableYouTubePrivacySetting(privacySetting));
          dispatch(
            updateYouTubePublishingParamPrivacySettings({
              idSocialMediaAccount: account.id,
              privacySettings: privacySetting,
            })
          );
        }
      },
    };
  };

  const listItems: ListItemStorage[] = [
    getListItemByPrivacySetting(YouTubePrivacySettings.PUBLIC),
    getListItemByPrivacySetting(YouTubePrivacySettings.UNLISTED),
    getListItemByPrivacySetting(YouTubePrivacySettings.PRIVATE, true),
  ];

  const getPrivacySelectorButton = (): ReactElement<any> => {
    return (
      <DropdownDefaultSelectBox
        id={`youtube-privacy-settings-selector-${account.id}`}
        size={DROPDOWN_SIZE.XSMALL}
        text={dropdownLabel}
        className={`_unmargin ${platformPublishFieldsStyles.roundedSelectBox}`}
      />
    );
  };

  const getPrivacySettingsList = (): ReactElement<any> => {
    return <List id={listId} className="spacing-m-0" draggable={false} multiSelect={false} items={listItems} keepAtleastOneItemSelected />;
  };

  const getPrivacyOptions = (): ReactElement<any> => {
    return (
      <Dropdown
        id={`youtube-social-posts-sort-${account.id}`}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        selector={getPrivacySelectorButton()}
        popup={getPrivacySettingsList()}
        popupClassName={styles.dropdown}
        popUpHasCustomWidth
        usePortal
      />
    );
  };

  useEffect(() => {
    if (!publishingParamsForAccount) {
      return;
    }

    if ('privacySettings' in publishingParamsForAccount) {
      const existingPrivacySetting = publishingParamsForAccount.privacySettings as YouTubePrivacySettings;
      setDropdownLabel(getReadableYouTubePrivacySetting(existingPrivacySetting));
      setSelectedPrivacySettings(existingPrivacySetting);
    }

    return (): void => {
      dispatch(clearList({id: listId}));
    };
  }, []);

  return (
    <div className="flex-row-align-center flex-content-between">
      <div className="flex-row-align-center">
        <Icon icon="icon-globe" className="spacing-m-r-1" type={IconType.NONE} size={IconSize.SIZE_ICON_16} />
        <Text val={window.i18next.t('pmwjs_privacy')} size={TextSize.MD} />
      </div>
      <div>{getPrivacyOptions()}</div>
    </div>
  );
}
